<!--
	Operations — the office's field-work workbench.

	It is where work is authored, sequenced, dispatched and closed (doc/in-progress/
	field-assignment-implementation-plan.md, phase P7): an Assignment is an office
	object before it is ever a technician's day, so the page reads and writes the
	§6 API and nothing else — there is no local mock state here, and no button acts
	without a request behind it.

	The page is one map with four floating surfaces over it:
	  1. the left Details pane — the record the ledger or the map selected; it
	     opens itself when a selection begins and collapses to its rail when the
	     operator is done with it;
	  2. the right authoring pane — the two tools that write a record rather than
	     read the district (New assignment, New route). It stands on the opposite
	     edge from the records a form is written against, so a ledger row or a
	     Details pane stays in view while the form is filled in, and either pane
	     can be collapsed without the other;
	  3. the bottom ledger — the district's four working lists as tables
	     (Assignments, Routes, Traps, Technicians), one tab at a time, dragged to
	     the height it is read at;
	  4. the map — the district boundary, the Sites, the trap instances (orange
	     while out, grey once collected), the ring of each standing station around
	     them, one line per route through its sequenced members, the open Work
	     Orders' H3 footprints, and the targets an Assignment is being authored
	     against.

	The page owns the district reads every surface states itself from (users, the
	Site ledger's points, trap configurations, trap stations, assignments,
	routes, trap instances) and the toast every failure path reports through, so a
	read is made once and a refusal is shown in the same place whichever surface
	raised it. A selection is not the page's: it is the shared workbench store,
	which the ledger, the map and the Details pane all read and write, so a map
	click and a ledger row click are the same act. The targets of an Assignment
	being authored are the page's state, because the map picks them and the form
	names them.
-->
<style scoped>
.topbar-content {
	display: flex;
	align-items: center;
	gap: 10px;
	height: 56px;
	padding: 0 16px;
	background: #fff;
	border-bottom: 1px solid #dee2e6;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

/* The day selector reads as one control: wide enough for a date, never growing
   into the title it sits beside. */
.day-picker {
	width: 11rem;
	flex: 0 0 auto;
}

/* The load failure is stated over the map, under the bar, so an empty pane is
   never mistaken for an empty district. */
.load-error {
	position: absolute;
	top: 64px;
	left: 12px;
	right: 12px;
	z-index: 13;
	margin: 0;
}
</style>

<template>
	<FullLayout>
		<template #topbar>
			<div class="topbar-content">
				<i class="bi bi-diagram-3"></i>
				<span class="fw-semibold">Operations Command Center</span>
				<span
					v-if="loading"
					class="spinner-border spinner-border-sm text-secondary"
					role="status"
					aria-label="Loading the district"
				></span>
				<!-- The day the board covers. A Route is not a calendar day
				     (decision 9), so the page names the window it reads rather
				     than assuming today: the schedule tab and the map's plans are
				     both readings of this day. -->
				<div class="d-flex align-items-center gap-2 ms-auto">
					<label class="small text-body-secondary mb-0" for="operations-day">
						Day
					</label>
					<input
						id="operations-day"
						v-model="dayDate"
						class="form-control form-control-sm day-picker"
						type="date"
						aria-label="Day the board covers"
					/>
					<button
						type="button"
						class="btn btn-sm btn-outline-secondary"
						:disabled="dayIsToday"
						@click="resetDay"
					>
						Today
					</button>
				</div>
			</div>
		</template>

		<!-- The page's own reads (technicians, Sites, trap configurations) are
		     what every form needs and none of them may invent; a failure to read
		     one is stated here rather than left to an empty pane. -->
		<div v-if="loadError" class="alert alert-danger load-error" role="alert">
			{{ loadError }}
		</div>

		<OperationsMap
			:assignments="assignments"
			:routes="routes"
			:stations="activeStations"
			:traps="visibleTraps"
			:sites="sites"
			:selection="selection"
			:selected-keys="selectedKeys"
			:selected-work-order-cells="selectedWorkOrderDetail?.cells ?? []"
			:pick-mode="mapPickMode"
			:targets="targets"
			:area-mode="areaMode"
			:area-kind="areaKind"
			:area="store.area"
			:positions="technicianPositions.positions"
			:show-technicians="showTechnicians"
			:plans="plans"
			@select="onMapSelect"
			@clear="onMapClear"
			@pick="addTarget"
			@drawn="addDrawnTarget"
			@area="onAreaSelection"
			@area-cancel="onAreaCancel"
			@select-technician="onTechnicianSelect"
		/>

		<!-- One pane, and it reads: the record the ledger or the map selected.
		     It opens itself when a selection begins; collapsing it is the
		     operator's own and leaves the selection where it is. Authoring is
		     not here — both create forms live on the right rail. -->
		<ToolRail
			side="left"
			v-model="leftTab"
			:tabs="railTabs"
			topOffset="72px"
			:bottomOffset="railBottomOffset"
			:raised="ledgerExpanded"
		>
			<template #details>
				<OperationsDetail
					:selection="selection"
					:users="users"
					:sites="sites"
					:trap-configurations="trapConfigurations"
					:assignments="assignments"
					:routes="routes"
					:stations="stations"
					:traps="traps"
					:work="work"
					:work-detail-row="workDetailRow"
					:selected-keys="selectedKeys"
					:plans="plans"
					@select="onDetailSelect"
					@close="closeDetails"
					@notify="showNotification"
					@changed="onChanged"
					@collect="collectTrap"
					@assign-work="assignWork"
					@set-trap="setTrapAtStation"
					@create-route="createRouteFromWork"
					@create-assignments="createAssignmentsFromWork"
					@route-from-assignments="createRouteFromAssignments"
				/>
			</template>

			<!-- The map's own tools: what a click does, and the area selection
			     that sweeps the tab the ledger has open. Both act on the map, so
			     they are stated here rather than in the details pane, which reads
			     one record at a time. -->
			<template #selection>
				<div class="fw-semibold small mb-1">Click on the map</div>
				<p class="form-text mb-2">
					Selects the trap, assignment, station, route or Work Order footprint
					under the pointer. Ctrl/Meta/Shift adds to the selection instead of
					replacing it; a click on empty map clears it.
				</p>

				<div class="fw-semibold small mb-1">Draw an area</div>
				<p class="small text-muted">
					Selects every {{ areaText.noun }} inside the drawn area, then narrows
					the ledger to them.
				</p>
				<p v-if="areaHint" class="form-text mb-2">{{ areaHint }}</p>
				<div class="d-grid gap-2">
					<button
						class="btn btn-sm"
						:class="
							areaMode === 'rectangle' ? 'btn-primary' : 'btn-outline-primary'
						"
						:disabled="areaDisabled"
						@click="toggleAreaMode('rectangle')"
					>
						<i class="bi bi-bounding-box me-1"></i>Draw rectangle
					</button>
					<button
						class="btn btn-sm"
						:class="
							areaMode === 'polygon' ? 'btn-primary' : 'btn-outline-primary'
						"
						:disabled="areaDisabled"
						@click="toggleAreaMode('polygon')"
					>
						<i class="bi bi-pentagon me-1"></i>Draw polygon
					</button>
				</div>
				<button
					class="btn btn-sm btn-outline-secondary w-100 mt-2"
					:disabled="store.count === 0"
					@click="store.clear()"
				>
					Clear selection
				</button>

				<div class="fw-semibold small mt-3 mb-1">Technician locations</div>
				<div class="form-check mb-2">
					<input
						id="operations-technicians"
						v-model="showTechnicians"
						class="form-check-input"
						type="checkbox"
					/>
					<label class="form-check-label small" for="operations-technicians">
						Show on the map
					</label>
				</div>
				<p class="small text-muted mb-1">
					Avatars show each technician's last known fix. The outline runs green
					(just now) through amber (an hour) to grey (two hours or older).
				</p>
				<p v-if="technicianPositions.error" class="small text-danger mb-0">
					Technician locations are unavailable right now.
				</p>
			</template>
		</ToolRail>

		<!-- The authoring rail, on the edge opposite the records it is written
		     against: a ledger row or a selected record stays readable on the
		     left and in the table below while the next piece of work is
		     composed here, and either pane collapses without taking the other
		     with it. -->
		<ToolRail
			side="right"
			v-model="rightTab"
			:tabs="createTabs"
			topOffset="72px"
			:bottomOffset="railBottomOffset"
			:raised="ledgerExpanded"
		>
			<template #assignment>
				<AssignmentForm
					ref="assignmentForm"
					:users="users"
					:sites="sites"
					:trap-configurations="trapConfigurations"
					:targets="targets"
					:pick-mode="pickMode"
					:work="doorWorkRows"
					:bulk="bulk"
					@notify="showNotification"
					@created="onAssignmentsCreated"
					@written="onAssignmentsWritten"
					@update:targets="targets = $event"
					@update:pickMode="pickMode = $event"
					@update:bulk="bulk = $event"
				/>
			</template>
			<template #route>
				<RouteCreateForm
					:users="users"
					:assignments="assignments"
					:sites="sites"
					:carry="routeCarry"
					@notify="showNotification"
					@created="onRouteCreated"
					@cancel="rightTab = null"
				/>
			</template>
		</ToolRail>

		<!-- The district's four working lists, as tables over the same map: one
		     tab at a time, dragged to the height it is read at through the shared
		     drawer widget, and the pane above ends where it begins. -->
		<OperationsLedger
			:assignments="assignments"
			:routes="routes"
			:work="work"
			:selected-keys="selectedKeys"
			:traps="visibleTraps"
			:users="users"
			:sites="sites"
			:selection="selection"
			:active-key="activeLedger"
			:fraction="ledgerFraction"
			:expanded="ledgerExpanded"
			:area-label="areaLabel"
			:area-keys="store.area ? store.multi : null"
			:plans="plans"
			:day-window="dayWindow"
			@update:active-key="activeLedger = $event"
			@select="onLedgerSelect"
			@toggle="onLedgerToggle"
			@update:fraction="ledgerFraction = $event"
			@update:expanded="setLedgerExpanded"
			@clear-area="store.clear()"
		/>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</FullLayout>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	onBeforeUnmount,
	onMounted,
	ref,
	watch,
} from "vue";

import { apiClient } from "@/client";
import FullLayout from "@/components/layout/Full.vue";
import type { LedgerRow } from "@/components/operations/ledger";
import AssignmentForm from "@/components/operations/AssignmentForm.vue";
import OperationsDetail from "@/components/operations/OperationsDetail.vue";
import OperationsLedger from "@/components/operations/OperationsLedger.vue";
import OperationsMap from "@/components/operations/OperationsMap.vue";
import RouteCreateForm from "@/components/operations/RouteCreateForm.vue";
import {
	defaultWindow,
	describeError,
	localInputValue,
	wireInstant,
} from "@/components/operations/wire";
import type {
	AuthoringState,
	MapPickTarget,
	RouteCarry,
	Selection,
	TargetDrawMode,
} from "@/components/operations/model";
import ToolRail from "@/components/panel/ToolRail.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { useWorkbenchSelection } from "@/map/select";
import type { AreaMode, MapSelectPayload, SelectId } from "@/map/select";
import { useTechnicianPositionStore } from "@/store/technician-position";
import type { GeoJSON } from "geojson";
import type {
	MapReviewQueryEnvelope,
	MapReviewSiteRow,
	MapReviewTrapInstanceRow,
	MapReviewTrapStationRow,
	OperationsPlans,
	OperationsWorkRow,
	RoutePlan,
	SyncAssignment,
	SyncRoute,
	TrapConfigurationResult,
	User,
	WorkOrderDetail,
} from "@/type/api";

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

// The district reads every surface states itself from. The Site ledger is read
// as its QUERY projection rather than the resource's list because that projection
// carries each Site's representative point — the vertex a site-targeted
// assignment contributes to its route's line — beside the display address the
// authoring form's select prints.
const users = ref<User[]>([]);
const sites = ref<MapReviewSiteRow[]>([]);
const trapConfigurations = ref<TrapConfigurationResult[]>([]);
const assignments = ref<SyncAssignment[]>([]);
const routes = ref<SyncRoute[]>([]);
const traps = ref<MapReviewTrapInstanceRow[]>([]);
const stations = ref<MapReviewTrapStationRow[]>([]);
const work = ref<OperationsWorkRow[]>([]);

// The day the board reads, in the operator's own zone: today's 08:00 to the same
// hour tomorrow unless the operator picks another day — `defaultWindow`'s
// convention. It is kept in the local `datetime-local` form the rest of the
// workbench states windows in, and converted to an instant only on the wire,
// because a `date` field and a `datetime-local` field both speak local time.
const dayWindow = ref(defaultWindow());

/** The picked day, as the `date` field states it. Picking a day moves the
 * window to that day's 08:00 → +24 h, so the two controls cannot disagree. */
const dayDate = computed({
	get: () => dayWindow.value.start.slice(0, 10),
	set: (value: string) => {
		if (!value) return;
		dayWindow.value = windowOn(value);
	},
});

/** The window a picked day covers. A day the field cannot parse leaves the
 * window where it is rather than reading yesterday's plans. */
function windowOn(day: string): { start: string; end: string } {
	const [year, month, date] = day.split("-").map(Number);
	if (!year || !month || !date) return dayWindow.value;
	const start = new Date(year, month - 1, date, 8, 0, 0, 0);
	return {
		start: localInputValue(start),
		end: localInputValue(new Date(start.getTime() + 24 * 60 * 60 * 1000)),
	};
}

const dayIsToday = computed(
	() => dayDate.value === defaultWindow().start.slice(0, 10),
);

function resetDay() {
	dayWindow.value = defaultWindow();
}

// Every plan the day board answered for, by route id. A plan is a derived
// reading of a Route (routing design §7), so the page holds it beside the Routes
// instead of in a store: the map draws the path, the ledger counts the day and
// the details pane states the ETAs, and all three read the same entry. A Route
// with no entry has no plan the page can state, and every surface falls back to
// what the page knew before plans existed — the straight line and "no plan yet".
const plans = ref<Map<number, RoutePlan>>(new Map());
let planToken = 0;

const loading = ref(true);
const loadError = ref("");
const authoring = ref<AuthoringState | null>(null);
const leftTab = ref<string | null>(null);
// The right rail's open pane. Route authoring is its own tool rather than a
// state of the Details pane: it is written while the ledger and the selected
// record are being read, so it lives on the other edge and closes on its own.
const rightTab = ref<string | null>(null);

// The one selection: which row the details pane reads, which rows the ledger has
// checked, and the ring an area selection is in force over. It is a pinia
// singleton, so this workbench claims it on setup — opening Operations never
// inherits what Planning or Map Review had selected.
const store = useWorkbenchSelection();
store.reset();

// The technician overlay: each member's last known Traccar fix, drawn as an
// avatar at the position of the newest one. The store is started when this page
// mounts and stopped when it goes, so a fix stored while nobody is looking at
// Operations costs no read; whether the pins are drawn at all is the operator's
// choice, and defaults on because the map's whole purpose is where people are.
const technicianPositions = useTechnicianPositionStore();
const showTechnicians = ref(true);

/** The store record, narrowed to the numeric row ids this page selects: every
 * object on the map is a server row, so a record naming anything else (an H3
 * cell, say) names nothing the panes can read. */
const selection = computed<Selection | null>(() => {
	const record = store.record;
	if (record === null || typeof record.id !== "number") return null;
	return { kind: record.kind as Selection["kind"], id: record.id };
});

/** The checked rows, as the ledger and the map both read them. */
const selectedKeys = computed(() => store.multi);

// The authoring session's map targets and the mode the map is in. They live on
// the page because two surfaces act on them: the map picks them and the form
// names, removes and submits them. One value each, so a chip and a map click
// cannot disagree about what is being authored.
const targets = ref<MapPickTarget[]>([]);
const pickMode = ref<TargetDrawMode>("pick");

// Whether the Assignment pane is authoring one assignment per checked Work row
// rather than against map targets. The page owns the switch because the map's
// picking has to be off while it is on: the rows are the targets.
const bulk = ref(false);

// What the Route tool carries in when a door in the Details pane opens it: the
// Work rows to compose, or the draft assignments to start the Route with. Bumped
// rather than flagged, so a second press after widening the selection carries
// only what is new; emptied when the pane closes or switches tools.
const routeCarry = ref<RouteCarry>(noCarry());

function noCarry(): RouteCarry {
	return { work: [], assignmentIds: [], technicianId: 0, seed: 0 };
}

// The map only picks while an Assignment is being authored against map targets:
// a selection's details, a Route's own pane and the bulk door's checked rows all
// leave the map to its ordinary reading.
const mapPickMode = computed<TargetDrawMode | null>(() =>
	authoring.value !== null && !bulk.value ? pickMode.value : null,
);

// The ledger tab the operator has open. The page owns it because the map's area
// selection has to know which collection a drawn ring sweeps: the tab names the
// features a ring may select.
const activeLedger = ref("work");

// The armed area tool, if any. Null is the map reading the collection.
const areaMode = ref<AreaMode | null>(null);

/** What a drawn area sweeps, per ledger tab. A tab with no map layer of its own
 * names null, and the tools say so rather than drawing a ring that selects
 * nothing. Work Orders are swept through their footprints; the Work tab lists
 * all three work kinds but only the Work Order footprints are drawn as work. */
const AREA_KIND_BY_TAB: Record<string, string | null> = {
	work: "work_order",
	assignments: "assignment",
	routes: "route",
	traps: "trap",
	technicians: null,
};

const areaKind = computed(() => AREA_KIND_BY_TAB[activeLedger.value] ?? null);

/** The tab's own name, and the noun a row of it is called, for the tools' prose:
 * a Work Order is swept through its footprint, so that tab's noun is the
 * footprint rather than the work item. */
const AREA_TAB_TEXT: Record<string, { title: string; noun: string }> = {
	work: { title: "Work", noun: "Work Order footprint" },
	assignments: { title: "Assignments", noun: "assignment" },
	routes: { title: "Routes", noun: "route" },
	traps: { title: "Traps", noun: "trap" },
	technicians: { title: "Technicians", noun: "technician" },
};

const areaText = computed(
	() => AREA_TAB_TEXT[activeLedger.value] ?? { title: "", noun: "row" },
);

/** Why the area tools cannot be used right now, or null when they can: a tab
 * with nothing drawn has no ring to sweep, and an authoring form already owns
 * the map's gestures. */
const areaHint = computed<string | null>(() => {
	if (authoring.value !== null) {
		return "Finish or close the form first: it owns the map's clicks.";
	}
	if (areaKind.value === null) {
		return `The ${areaText.value.title} tab has nothing drawn to select.`;
	}
	return null;
});

const areaDisabled = computed(() => areaHint.value !== null);

/** The drawn area as the ledger's own clause: named with what it selected, so
 * the chip and the highlighted rows cannot disagree. */
const areaLabel = computed(() =>
	store.area === null ? null : `Drawn area · ${store.count} selected`,
);

// Leaving a tab takes its swept-up selection with it: a ring drawn over
// Assignments must not narrow Routes.
watch(activeLedger, () => {
	areaMode.value = null;
	store.clear();
});

// The Work Order read on demand. A footprint is drawn for every open Work Order,
// while the Work queue lists the work an office dispatches, so a clicked
// footprint can name a row the page has never read; this is what the details
// pane reads for it, and what the map frames.
const selectedWorkOrderDetail = ref<WorkOrderDetail | null>(null);
let workDetailToken = 0;

watch(
	() => store.record,
	async (record) => {
		const token = ++workDetailToken;
		selectedWorkOrderDetail.value = null;
		if (
			record === null ||
			record.kind !== "work_order" ||
			typeof record.id !== "number"
		) {
			return;
		}
		try {
			const detail = await apiClient.JSONGet<WorkOrderDetail>(
				`/api/work-order/${record.id}`,
			);
			if (token === workDetailToken) selectedWorkOrderDetail.value = detail;
		} catch {
			// The queue row (when there is one) still renders; the footprint
			// simply does not frame.
		}
	},
);

/** The fetched work order, as the row the details pane renders: the same shape
 * the Work queue builds, so `WorkDetails` reads one kind of row. */
const workDetailRow = computed<OperationsWorkRow | null>(() => {
	const detail = selectedWorkOrderDetail.value;
	if (detail === null) return null;
	return {
		origin: "work_order",
		id: detail.id,
		title: detail.description,
		state: detail.state,
		priority: detail.priority,
		due_at: detail.due_at,
		target_summary:
			detail.cells.length > 0 ? "H3 footprint" : "no H3 footprint",
	};
});

/** The Work rows a create door carries: the checked ones, or — when nothing is
 * checked — the single Work record the Details pane is reading. The pane's own
 * doors and the Assignment tool's bulk switch both read this, so what the pane
 * lists is what a door starts from. A Work Order read on demand joins the pool,
 * so a footprint clicked on the map is a row a door can carry too. */
const workPool = computed(() => {
	const detail = workDetailRow.value;
	if (detail === null) return work.value;
	const present = work.value.some(
		(row) =>
			workKind(row.origin) === workKind(detail.origin) && row.id === detail.id,
	);
	return present ? work.value : [detail, ...work.value];
});

const doorWorkRows = computed<OperationsWorkRow[]>(() => {
	const checked = workPool.value.filter((row) =>
		selectedKeys.value.has(`${workKind(row.origin)}:${row.id}`),
	);
	if (checked.length > 0) return checked;
	const record = selection.value;
	if (record === null) return [];
	return workPool.value.filter(
		(row) => workKind(row.origin) === record.kind && row.id === record.id,
	);
});

// The ledger's height, as the fraction of the page it covers — the same reading
// Map Review's ledger drags by. The rail above ends where the ledger begins.
const ledgerFraction = ref(0.34);
const ledgerExpanded = computed(() => ledgerFraction.value >= 0.99);

function setLedgerExpanded(expanded: boolean) {
	ledgerFraction.value = expanded ? 1 : 0.34;
}

// A retired station is out of the trapping network: its ring is not drawn and
// its instances are not listed, because both are offers to visit it. The rows
// stay in `stations`/`traps` so a selection made before a retirement still
// resolves in the Details pane.
const activeStations = computed(() =>
	stations.value.filter((station) => station.active),
);

const retiredStationIds = computed(
	() =>
		new Set(
			stations.value
				.filter((station) => !station.active)
				.map((station) => station.id),
		),
);

// An off-station instance (station_id null) belongs to no station, so no
// retirement can exclude it.
const visibleTraps = computed(() =>
	traps.value.filter(
		(trap) =>
			trap.station_id === null || !retiredStationIds.value.has(trap.station_id),
	),
);

// The pane has two tabs: Details, the reader for whatever is selected, and the
// map's own tools. Authoring is not a tab here — both create forms live on the
// right rail, so this pane never stops being a reader.
const railTabs = [
	{
		key: "details",
		icon: "bi bi-card-text",
		title: "Details",
		bodyWidth: "400px",
	},
	{
		key: "selection",
		icon: "bi bi-tools",
		title: "Map tools",
		bodyWidth: "400px",
	},
];

// The right rail holds the page's two authoring tools. A record is written
// rather than selected, so each icon is always on the rail: opening one neither
// needs a selection nor disturbs one.
const createTabs = [
	{
		key: "assignment",
		icon: "bi bi-clipboard-plus",
		title: "New assignment",
		bodyWidth: "400px",
	},
	{
		key: "route",
		icon: "bi bi-signpost-split",
		title: "New route",
		bodyWidth: "400px",
	},
];

// The tab strip is a header's 43px: 6px of padding above and below its 30px
// tab, plus the hairline under it. The pane never grows a negative height when
// the ledger is dragged to the top — it stands as that header alone.
const RAIL_HEADER_HEIGHT = 43;

// The pane ends where the ledger begins, so the two floating surfaces never
// cover one another. The ledger's height is the fraction it is dragged to, so
// the pane follows the drag live instead of stepping between presets.
const railBottomOffset = computed(
	() =>
		`min(calc(${(ledgerFraction.value * 100).toFixed(2)}vh + 12px), ` +
		`calc(100% - ${72 + RAIL_HEADER_HEIGHT}px))`,
);

// A selection is a reason to read the left pane, so it opens itself; the
// operator collapses it when done. Closing an authoring session takes the
// picking session with it: a target picked for a form that has gone away is not
// a target for the next one.
watch([selection, authoring], ([nextSelection, nextAuthoring]) => {
	if (nextSelection !== null) leftTab.value = "details";
	if (nextAuthoring === null) {
		targets.value = [];
		pickMode.value = "pick";
	}
});

// The bulk door replaces the map's targets with the checked rows, so a pick made
// before it was switched on is dropped: two target sets on screen, only one of
// which would be written, is the state this avoids.
watch(bulk, (on) => {
	if (on) targets.value = [];
});

// The two authoring panes answer one question — what the district is about to
// be told to do — so opening either ends the other: one map cannot serve two
// forms. The Assignment pane opened from its own icon has nothing to fill in,
// so it starts a blank session; the pre-fill doors call `openAssignment` with
// the record they were started from. Reading the district does not end either
// pane: a ledger row, a map click and a Details pane all leave it open, which is
// what putting them on the opposite edge is for.
watch(rightTab, (tab) => {
	if (tab === "assignment") {
		if (authoring.value === null) openAssignment();
		routeCarry.value = noCarry();
		return;
	}
	if (tab === null) routeCarry.value = noCarry();
	authoring.value = null;
	bulk.value = false;
});

// The Assignment form is one instance the pre-fill doors reuse, so it is told
// what it is filling in once it is on screen (the ref exists only while the pane
// renders).
const assignmentForm = ref<InstanceType<typeof AssignmentForm> | null>(null);

watch(
	() => authoring.value,
	async (state) => {
		await nextTick();
		const form = assignmentForm.value;
		if (!form || !state) return;
		if (state.kind === "collect") form.startCollect(state.trap);
		else if (state.kind === "assignment") {
			form.startForTechnician(state.technicianId ?? 0);
		} else if (state.kind === "set_trap") {
			form.startForSite(state.siteId);
		}
	},
);

/** A target clicked on the map. The same Site or parcel clicked twice is one
 * target rather than two Assignments for the same place. */
function addTarget(target: MapPickTarget) {
	const duplicate = targets.value.some((existing) => {
		if (existing.kind === "site" && target.kind === "site") {
			return existing.siteId === target.siteId;
		}
		if (existing.kind === "parcel" && target.kind === "parcel") {
			return (
				existing.externalId === target.externalId &&
				existing.region === target.region
			);
		}
		return false;
	});
	if (duplicate) return;
	targets.value = [...targets.value, target];
}

/** A drawn point or area. An Assignment carries one drawn geometry, so at most
 * one is targeted per submit and a second draw replaces the first. */
function addDrawnTarget(geometry: GeoJSON.Point | GeoJSON.Polygon) {
	const drawn: MapPickTarget =
		geometry.type === "Point"
			? { kind: "point", geometry }
			: { kind: "area", geometry };
	targets.value = [
		...targets.value.filter(
			(target) => target.kind !== "point" && target.kind !== "area",
		),
		drawn,
	];
}

let readToken = 0;

// One read of everything the workbench draws. The assignment collection is read
// whole because it is the working set three surfaces state themselves from — the
// technician load, the route lines' vertices, and the details of whatever is
// selected — and a route's members are not otherwise resolvable without a read
// per member.
async function readDistrict() {
	const token = ++readToken;
	const [
		userList,
		sitePage,
		configurations,
		assignmentPage,
		routePage,
		stationPage,
		openTrapPage,
		collectedTrapPage,
	] = await Promise.all([
		apiClient.JSONGet<User[]>("/api/user"),
		apiClient.JSONQueryAll<MapReviewSiteRow>("/api/site", { limit: 500 }),
		apiClient.JSONGet<TrapConfigurationResult[]>("/api/trap-configuration"),
		apiClient.JSONQueryAll<SyncAssignment>("/api/assignment", { limit: 500 }),
		// A cancelled Route is history: dispatching, sequencing and membership
		// are all finished for it, and its discarded work has already returned to
		// the unassigned pool. The workbench's working set is the Routes still in
		// play, so the read excludes cancelled ones rather than drawing lines and
		// ledger rows nothing can act on.
		apiClient.JSONQueryAll<SyncRoute>("/api/route", {
			limit: 200,
			order: [{ property: "window_start", direction: "desc" }],
			filter: [
				{ property: "state", operator: "not-equal", value: "cancelled" },
			],
		}),
		apiClient.JSONQueryAll<MapReviewTrapStationRow>("/api/trap-station", {
			limit: 500,
		}),
		apiClient.JSONQueryAll<MapReviewTrapInstanceRow>("/api/trap-instance", {
			limit: 500,
			filter: [{ property: "state", operator: "equal", value: "set" }],
		}),
		apiClient.JSONQuery<MapReviewQueryEnvelope<MapReviewTrapInstanceRow>>(
			"/api/trap-instance",
			{
				limit: 100,
				order: [{ property: "date", direction: "desc" }],
				filter: [{ property: "state", operator: "equal", value: "collected" }],
			},
		),
	]);
	const workPage = await apiClient.JSONGet<{ work: OperationsWorkRow[] }>(
		"/api/operations/work",
	);
	if (token !== readToken) return;
	users.value = userList ?? [];
	sites.value = sitePage.items;
	trapConfigurations.value = configurations ?? [];
	assignments.value = assignmentPage.items;
	routes.value = routePage.items;
	stations.value = stationPage.items;
	traps.value = [...openTrapPage.items, ...collectedTrapPage.items];
	work.value = workPage.work ?? [];
	const liveKeys = new Set(
		work.value.map((row) => `${workKind(row.origin)}:${row.id}`),
	);
	// A Lead or direct Assignment the queue no longer carries is a name the panes
	// cannot resolve: its row is gone and this read says so, so the selection goes
	// with it rather than pointing the details pane at nothing. A Work Order is
	// not cleaned up here: the map draws every open footprint, and a selected one
	// the queue does not dispatch is read on demand, so its row exists even when
	// this list has none.
	const record = store.record;
	if (
		record !== null &&
		(record.kind === "work_lead" || record.kind === "work_direct") &&
		!liveKeys.has(`${record.kind}:${record.id}`)
	) {
		store.clear();
	}
	// The same reading for Routes. The read drops cancelled ones, so a route
	// selection that no longer resolves is exactly the one just discarded: the
	// pane returns to "nothing selected" instead of naming a record the ledger and
	// the map have both stopped drawing.
	if (
		record !== null &&
		record.kind === "route" &&
		typeof record.id === "number" &&
		!routes.value.some((route) => route.id === record.id)
	) {
		store.clear();
	}
}

function workKind(origin: string): string {
	return origin === "lead"
		? "work_lead"
		: origin === "work_order"
			? "work_order"
			: "work_direct";
}

/**
 * The day board: every Route overlapping the picked day, with its plan. The
 * answer replaces the map rather than merging into it — a Route the board left
 * out (cancelled, or outside the day) is not planned for this day, and a kept
 * entry would draw a path nothing computed.
 *
 * The window goes out as an instant although it is stated locally: the body is
 * decoded into `time.Time` on the server.
 */
async function loadPlans() {
	const start = wireInstant(dayWindow.value.start);
	const end = wireInstant(dayWindow.value.end);
	if (start === null || end === null) return;
	const token = ++planToken;
	try {
		const board = await apiClient.JSONPost<OperationsPlans>(
			"/api/operations/plans",
			{ window_start: start, window_end: end },
		);
		if (token !== planToken) return;
		plans.value = new Map<number, RoutePlan>(
			board.plans.map((plan) => [plan.routeId, plan]),
		);
	} catch (e: unknown) {
		if (token !== planToken) return;
		// The board is a reading, not a gate: a refusal leaves the map's straight
		// lines in place and says why once, through the page's toast.
		showNotification("Day board not read", describeError(e));
	}
}

/**
 * Recompute one Route's plan and hold the answer. An engine that is down or
 * angry still answers with a plan (`unavailable` / `failed`), so a refusal here
 * is the request itself failing: the entry stays as it stands — the map keeps
 * drawing it — and the toast says what happened.
 */
async function refreshPlan(routeId: number) {
	try {
		const plan = await apiClient.JSONPost<RoutePlan>(
			`/api/route/${routeId}/plan`,
			{},
		);
		plans.value = new Map<number, RoutePlan>(plans.value).set(
			plan.routeId,
			plan,
		);
	} catch (e: unknown) {
		showNotification("Drive plan not computed", describeError(e));
	}
}

/**
 * The board plus the selected Route: a plan is a projection of a Route, so
 * anything that changed a Route changed its plan. The selected Route is read
 * separately because the board only covers one day and the pane states the plan
 * of whatever it is showing.
 */
async function refreshPlans() {
	await loadPlans();
	const selected = selection.value;
	if (selected?.kind === "route") await refreshPlan(selected.id);
}

// A picked day is a different board, and the selected Route may not be in it, so
// the day is read together with that Route rather than instead of it.
watch(dayWindow, () => {
	void refreshPlans();
});

// A Route named by the pane is read for its plan as soon as it is named: the
// board may not cover its day, and the pane's job is to state the plan of what
// it is showing.
watch(
	() => (selection.value?.kind === "route" ? selection.value.id : null),
	(routeId) => {
		if (routeId !== null) void refreshPlan(routeId);
	},
);

async function load() {
	loading.value = true;
	loadError.value = "";
	try {
		await readDistrict();
		void loadPlans();
	} catch (e: unknown) {
		loadError.value = describeError(e);
		showNotification("Operations data not read", loadError.value);
	} finally {
		loading.value = false;
	}
}

// Every write re-reads rather than patching a copy: a state move, a membership
// change and a created row all change counts and geometry that more than one
// surface states.
async function reload() {
	try {
		await readDistrict();
		loadError.value = "";
	} catch (e: unknown) {
		loadError.value = describeError(e);
		showNotification("Operations data not read", loadError.value);
	}
}

/** Put a row the server just wrote into the collection that renders it,
 * replacing its previous copy if the read already brought one in. */
function upsert<T extends { id: number }>(rows: T[], row: T): T[] {
	const index = rows.findIndex((existing) => existing.id === row.id);
	if (index === -1) return [row, ...rows];
	const next = rows.slice();
	next[index] = row;
	return next;
}

/** A map click resolved by the shared widget: the object under the pointer, and
 * whether the gesture was Ctrl/Meta/Shift. It is a read, not an intent: an
 * authoring pane stays open beside it. */
function onMapSelect(payload: MapSelectPayload) {
	store.select(payload.kind, payload.id, { additive: payload.additive });
}

/** A technician's avatar clicked on the map: the same act as clicking their
 * ledger row, so it goes through the store. The pin already stays silent while a
 * form owns the map's gestures, and the guard is repeated here where the select
 * is made — opening a form and the click that lands on a pin are ordered by
 * nobody, and a click that raced the form must not steal its map. */
function onTechnicianSelect({
	id,
	additive,
}: {
	id: number;
	additive: boolean;
}) {
	if (authoring.value !== null) return;
	store.select("technician", id, { additive });
}

/** The details pane naming another row (a technician's route, a route's stop):
 * the same act as clicking that row, so it goes through the store. The authoring
 * panes stay open: the record named here is the context the next write is
 * decided against. */
function onDetailSelect(next: Selection) {
	store.select(next.kind, next.id);
}

/** A click on empty map. It is not a decision to throw away a half-written
 * form, so while one is open it is ignored. */
function onMapClear() {
	if (authoring.value) return;
	store.clear();
}

/** A drawn area selects every object of the active ledger tab inside it. */
function onAreaSelection(payload: {
	kind: string;
	ids: SelectId[];
	ring: [number, number][];
}) {
	store.setArea(
		payload.ring,
		payload.ids.map((id) => `${payload.kind}:${id}`),
	);
	if (payload.ids.length > 0) {
		// The pane reads one object, so the record follows the first row swept
		// up. The select is additive precisely so it does not replace the set the
		// ring just wrote; the record is the only thing it changes.
		store.select(payload.kind, payload.ids[0], { additive: true });
	}
	areaMode.value = null;
	leftTab.value = "details";
}

/** The tool was disarmed without a commit (Escape, or a tab change). */
function onAreaCancel() {
	areaMode.value = null;
}

/** Arm the tool, or disarm it when it is already the armed one. */
function toggleAreaMode(mode: AreaMode) {
	areaMode.value = areaMode.value === mode ? null : mode;
}

/** The one door onto the Assignment pane. Opening it starts a fresh picking
 * session: the targets and the mode belong to the form about to open, never to
 * the one it replaces, and an armed area tool is disarmed — the map answers one
 * gesture at a time. A Route half-written on the right ends here: one authoring
 * intent at a time. */
function openAssignment(state: AuthoringState = { kind: "assignment" }) {
	targets.value = [];
	pickMode.value = "pick";
	areaMode.value = null;
	bulk.value = false;
	authoring.value = state;
	rightTab.value = "assignment";
}

// A trap's details' one action: open the authoring form
// pre-filled from the instance. The instance keeps sitting there until something
// collects it — opening a form creates nothing (decision 6).
function collectTrap(trap: MapReviewTrapInstanceRow) {
	openAssignment({ kind: "collect", trap });
}

function assignWork(technicianId: number) {
	openAssignment({ kind: "assignment", technicianId });
}

// A station's details' one action. The work is placed at the Site the station
// stands on, because a Site — not a station — is what an Assignment targets;
// the station only supplied the place the operator started from.
function setTrapAtStation(siteId: number) {
	openAssignment({ kind: "set_trap", siteId });
}

/** The Details pane's doors onto the Route tool. Each one carries the selection
 * the pane is showing into the form — Work rows become composed work items, draft
 * assignments become the Route's starting membership — so the rows stay in view
 * beside the form rather than being re-entered inside it. */
function createRouteFromWork() {
	routeCarry.value = {
		work: doorWorkRows.value,
		assignmentIds: [],
		technicianId: 0,
		seed: routeCarry.value.seed + 1,
	};
	rightTab.value = "route";
}

function createRouteFromAssignments(assignmentIds: number[]) {
	const carried = assignments.value.filter((row) =>
		assignmentIds.includes(row.id),
	);
	routeCarry.value = {
		work: [],
		assignmentIds,
		technicianId: carried.length > 0 ? carried[0].technicianId : 0,
		seed: routeCarry.value.seed + 1,
	};
	rightTab.value = "route";
}

/** The Work pane's other door: the same rows, authored as assignments instead of
 * a Route, so the Assignment tool opens with its bulk switch already on. */
function createAssignmentsFromWork() {
	openAssignment();
	bulk.value = true;
}

/** A ledger row carries what clicking it selects; the store is what that means,
 * so a table row and a map marker land in the pane identically. The authoring
 * panes stay open: reading a row is what a Route or an Assignment is written
 * against. */
function onLedgerSelect(row: LedgerRow, additive: boolean) {
	if (additive) {
		onLedgerToggle(row);
		return;
	}
	store.select(row.kind, row.id);
}

/** The ledger's checkbox. A row that was just checked is the one the pane reads,
 * so the record follows it; the select is additive, which leaves the set the
 * checkbox wrote exactly as it is. Unchecking the last row clears the record with
 * the set, and the pane's own "nothing selected" reading takes over. */
function onLedgerToggle(row: LedgerRow) {
	const wasChecked = store.has(row.kind, row.id);
	store.toggle(row.kind, row.id);
	if (!wasChecked) store.select(row.kind, row.id, { additive: true });
}

// The reader's own close: nothing selected to read, so the pane goes away and
// any armed area tool with it. Authoring is not this pane's to close — the
// right rail owns its own chevron.
function closeDetails() {
	store.clear();
	leftTab.value = null;
	areaMode.value = null;
}

// Every write re-reads rather than patching a copy, and re-reads the plans with
// it: a state move, a membership change, a reorder and an edited window all
// change what the Route's plan says, and the plan is the one reading the page
// does not own.
async function onChanged() {
	await reload();
	void refreshPlans();
}

// A create answers with the rows it wrote, so those rows are shown in the same
// tick the form closes: the assignment appears in the ledger, on the map and in
// the left pane before any read comes back, and the pane is never left empty
// while a request is in flight. The district read still runs afterwards — the
// Route a composed Assignment joined, and anything else a surface derives, must
// reconcile — but nothing waits on it.
//
// A batch that only partly landed does not close the pane: the form is still
// holding the rows that failed, and the operator's retry belongs where the
// failure is stated.
function onAssignmentsCreated(created: SyncAssignment[]) {
	rightTab.value = null;
	recordAssignments(created);
}

function onAssignmentsWritten(created: SyncAssignment[]) {
	recordAssignments(created);
}

function recordAssignments(created: SyncAssignment[]) {
	if (created.length === 0) return;
	assignments.value = created.reduce(
		(rows, row) => upsert(rows, row),
		assignments.value,
	);
	store.select("assignment", created[0].id);
	void reload();
}

// A new Route has no plan yet, and the pane is about to state one: the select
// below is what reads it — the selection watcher asks for the plan of whatever
// Route the pane names, which is the single trigger for every way a Route gets
// selected. The authoring pane closes on its own answer: a create is finished
// business, and the Route it wrote is what the operator reads next.
function onRouteCreated(created: SyncRoute) {
	routes.value = upsert(routes.value, created);
	rightTab.value = null;
	store.select("route", created.id);
	void reload();
}

onMounted(load);

// The overlay's stream is followed only while this page is open: an accepted fix
// is a frequent event, and a subscription that outlived the page would re-read the
// roster for a surface nobody is looking at. `start` also reads once, so the pins
// are there before the first event arrives; `stop` leaves the rows in place for
// the next visit.
onMounted(() => {
	technicianPositions.start();
});

onBeforeUnmount(() => technicianPositions.stop());
</script>
